import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, X, CheckCircle2, AlertTriangle, Trash2, TrendingUp, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDividendGateAuditButton — May 2026 (Phase E3.2 UI).
 *
 * Admin-only button that hits GET /api/admin/database-audit/dividend-gate
 * and pops a modal listing every ticker in `stock_cache` that fails the
 * 5-field dividend gate (yield > 0, rate > 0, frequency, ex-date, pay-date).
 *
 * The endpoint is READ-ONLY: this surface lets the admin spot data anomalies
 * (e.g. a stock that quietly suspended its dividend) and decide what to do
 * — there is no auto-purge from the modal.
 */

const BUCKET_LABELS = {
  missing_yield: 'Missing dividend_yield',
  missing_rate: 'Missing dividend_rate ($/share)',
  missing_frequency: 'Missing frequency',
  missing_ex_date: 'Missing ex-dividend date',
  missing_pay_date: 'Missing pay date',
};

function FailureBucket({ label, samples, totalInBucket, onBlocklist, onMarkNonDiv, onPromote, onTickerClick, busyTicker, selected, onToggle, disableChecks }) {
  if (!samples || samples.length === 0) return null;
  const hidden = Math.max(0, totalInBucket - samples.length);
  return (
    <div className="border border-border/60 rounded-md overflow-hidden">
      <div className="px-3 py-2 bg-muted/40 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wide text-foreground">
          {label}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {samples.length} shown{hidden > 0 ? ` · +${hidden} more` : ''}
        </div>
      </div>
      <div className="divide-y divide-border/40">
        {samples.map((row) => (
          <div
            key={row.ticker}
            className="px-3 py-1.5 flex items-center gap-3 text-xs"
            data-testid={`audit-row-${row.ticker}`}
          >
            {/* Round P112a (Jul 2026) — Per-row checkbox for bulk
                reclassify. Sits BEFORE the rising-arrow so the
                selection column is unambiguously separate from the
                action column. */}
            <input
              type="checkbox"
              checked={selected?.has(row.ticker) || false}
              onChange={() => onToggle?.(row.ticker)}
              disabled={disableChecks}
              className="w-3.5 h-3.5 accent-amber-500 cursor-pointer shrink-0"
              data-testid={`audit-checkbox-${row.ticker}`}
              aria-label={`Select ${row.ticker}`}
            />
            {/* Round P111z (Feb 28, 2026) — Non-Dividend reclassification
                moved to FIRST position (was between Shield and Trash).
                Physical separation from the Trash icon prevents the
                muscle-memory "click far right = trash" reflex from
                accidentally destroying a row when the admin only meant
                to send it to the Pending Non-Div review queue. The rising
                arrow is now the SAFE default action on the left edge;
                Shield (lock as dividend) and Trash (permanent blocklist)
                stay grouped on the right. */}
            <button
              type="button"
              onClick={() => onMarkNonDiv(row)}
              disabled={busyTicker === row.ticker}
              className="text-muted-foreground hover:text-emerald-600 disabled:opacity-40 shrink-0"
              title={`Move ${row.ticker} to the Pending Non-Dividend Review queue. Removes it from stock_cache; open the Pending Non-Div button to Accept and hydrate as a non-dividend stock.`}
              data-testid={`audit-mark-non-div-${row.ticker}`}
            >
              {busyTicker === row.ticker
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <TrendingUp className="w-3.5 h-3.5" />}
            </button>
            {/* Round 96c (Feb 2026) — Ticker is now a button that opens
                the StockModal via the Dashboard's onStockClick handler,
                so the admin can inspect a flagged ticker without losing
                the audit list or page state. */}
            <button
              type="button"
              onClick={() => onTickerClick?.(row.ticker)}
              className="font-mono font-bold w-14 shrink-0 text-yellow-700 dark:text-yellow-300 hover:text-yellow-500 dark:hover:text-yellow-200 underline-offset-2 hover:underline text-left"
              title={`Open ${row.ticker} in the Stock Modal`}
              data-testid={`audit-ticker-open-${row.ticker}`}
            >
              {row.ticker}
            </button>
            <span className="flex-1 truncate text-muted-foreground" title={row.name}>
              {row.name}
            </span>
            <span className="font-mono text-[10px] text-muted-foreground shrink-0">
              y={row.dividend_yield ?? '—'} · r={row.dividend_rate ?? '—'} · f={row.frequency ?? '—'}
            </span>
            {row.all_misses && row.all_misses.length > 1 && (
              <span
                className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0 rounded bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 shrink-0"
                title={row.all_misses.join(', ')}
              >
                <AlertTriangle className="w-2.5 h-2.5" />
                {row.all_misses.length} fields
              </span>
            )}
            {/* Round 96c (Feb 2026) — Promote → lock as dividend. Re-
                fetches Massive + Yahoo, re-runs the 5-field gate, and
                if it passes flips the row's lock fields so it stops
                showing up in this audit. Use when the ticker IS a
                real dividend payer (the audit just caught stale or
                partial cache data). */}
            <button
              type="button"
              onClick={() => onPromote(row)}
              disabled={busyTicker === row.ticker}
              className="text-muted-foreground hover:text-sky-600 disabled:opacity-40 shrink-0"
              title={`Promote ${row.ticker} into the locked dividend DB (refreshes from Massive, re-runs the 5-field gate, locks on pass).`}
              data-testid={`audit-promote-${row.ticker}`}
            >
              {busyTicker === row.ticker
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <ShieldCheck className="w-3.5 h-3.5" />}
            </button>
            {/* Round 94f — Trash button: drops the ticker from
                stock_cache + every dependent collection AND adds it to
                the permanent dynamic blocklist so it can never come
                back. Survives redeploys. Reserve for genuinely-defunct
                / illiquid tickers (e.g. wind-down CEFs). */}
            <button
              type="button"
              onClick={() => onBlocklist(row)}
              disabled={busyTicker === row.ticker}
              className="text-muted-foreground hover:text-rose-600 disabled:opacity-40 shrink-0"
              title={`Permanently remove ${row.ticker} from the database and add to the blocklist (never re-add).`}
              data-testid={`audit-blocklist-${row.ticker}`}
            >
              {busyTicker === row.ticker
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDividendGateAuditButton({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);
  // Confirm-prompt state (replaces window.confirm() which is unreliable
  // inside embedded contexts). When set to a {ticker, name} object,
  // a modal asks the admin to confirm permanent removal.
  const [confirmBlocklist, setConfirmBlocklist] = useState(null);
  // Round P112a (Jul 2026) — Bulk-reclassify selection state. Mirrors
  // the AdminPendingDividendReviewButton "Add Checked" pattern but for
  // the SAFE reclassify-to-non-div action (rising arrow). Per user:
  // most audit failures are non-dividend stocks that should end up in
  // the Pending Non-Div queue. The bulk button iterates alphabetically
  // through the checked tickers calling the same single-row
  // mark-non-dividend endpoint one at a time.
  const [selected, setSelected] = useState(new Set());
  const [bulkReclassifying, setBulkReclassifying] = useState(false);

  // Flatten the bucketed failure_samples into a single set of visible
  // tickers so the master-checkbox / bulk-action / count logic can
  // reason about the modal as a flat list. Order matches the buckets'
  // render order (missing_yield → missing_rate → missing_frequency →
  // missing_ex_date → missing_pay_date). Deduped by ticker in case a
  // row somehow appears in multiple buckets.
  const visibleTickers = useMemo(() => {
    if (!report?.failure_samples) return [];
    const seen = new Set();
    const out = [];
    Object.values(report.failure_samples).forEach((rows) => {
      (rows || []).forEach((r) => {
        if (r?.ticker && !seen.has(r.ticker)) {
          seen.add(r.ticker);
          out.push(r.ticker);
        }
      });
    });
    return out;
  }, [report]);

  const allVisibleSelected =
    visibleTickers.length > 0 && visibleTickers.every((t) => selected.has(t));

  const toggleOne = (ticker) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(ticker)) n.delete(ticker);
      else n.add(ticker);
      return n;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) {
        visibleTickers.forEach((t) => n.delete(t));
      } else {
        visibleTickers.forEach((t) => n.add(t));
      }
      return n;
    });
  };

  if (user?.role !== 'admin') return null;

  const runAudit = async () => {
    setLoading(true);
    setReport(null);
    setOpen(true);
    try {
      const { data } = await axios.get(`${API}/admin/database-audit/dividend-gate`, { withCredentials: true });
      setReport(data);
    } catch (err) {
      toast.error('Audit failed', {
        description: err?.response?.data?.detail || err?.message,
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Round 94f — Permanent blocklist add. Drops the ticker from
  // stock_cache and all dependent collections AND adds it to the
  // persistent dynamic blocklist so a future Yahoo / Massive refresh
  // can never re-insert it. Removes the row from the local audit
  // report immediately so the admin sees their action take effect
  // without re-running the full audit.
  const blocklistTicker = async (row) => {
    const t = row.ticker;
    setBusyTicker(t);
    try {
      const { data } = await axios.post(
        `${API}/admin/blocklist/${encodeURIComponent(t)}`,
        { reason: 'audit_dividend_gate' },
        { withCredentials: true },
      );
      toast.success(`Blocklisted ${t}`, {
        description: `Removed from ${Object.keys(data.removed || {}).length} collections. Blocklist size: ${data.blocklist_size}.`,
        duration: 4000,
      });
      // Strip the row out of every bucket so the modal updates live.
      setReport((prev) => {
        if (!prev) return prev;
        const fs = { ...(prev.failure_samples || {}) };
        const fb = { ...(prev.failure_buckets || {}) };
        Object.keys(fs).forEach((k) => {
          const before = fs[k].length;
          fs[k] = fs[k].filter((r) => r.ticker !== t);
          if (fb[k] != null && fs[k].length !== before) {
            fb[k] = Math.max(0, fb[k] - (before - fs[k].length));
          }
        });
        return {
          ...prev,
          failure_samples: fs,
          failure_buckets: fb,
          failing_gate: Math.max(0, (prev.failing_gate || 1) - 1),
          total_in_cache: Math.max(0, (prev.total_in_cache || 1) - 1),
        };
      });
    } catch (err) {
      toast.error(`Failed to blocklist ${t}`, {
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        duration: 5000,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Round 96b (Feb 2026) — Non-Dividend reclassification handler.
  // Calls POST /api/admin/mark-non-dividend/{ticker}. Instead of
  // permanently removing the ticker, flips `pays_dividend=False` in
  // stock_cache and clears the stale dividend fields. The row is then
  // immediately stripped from the audit modal (same UX as the trash
  // button) since it no longer fails the gate — it's just not a
  // dividend stock.
  const markNonDiv = async (row) => {
    const t = row.ticker;
    setBusyTicker(t);
    try {
      const { data } = await axios.post(
        `${API}/admin/mark-non-dividend/${encodeURIComponent(t)}`,
        { reason: 'audit_reclassified_non_div' },
        { withCredentials: true },
      );
      toast.success(`${t} → Pending Non-Div Review`, {
        description: `Queued for review — open the "Pending Non-Dividend Review" button and click Accept to hydrate it as a non-dividend stock.`,
        duration: 4500,
      });
      setReport((prev) => {
        if (!prev) return prev;
        const fs = { ...(prev.failure_samples || {}) };
        const fb = { ...(prev.failure_buckets || {}) };
        Object.keys(fs).forEach((k) => {
          const before = fs[k].length;
          fs[k] = fs[k].filter((r) => r.ticker !== t);
          if (fb[k] != null && fs[k].length !== before) {
            fb[k] = Math.max(0, fb[k] - (before - fs[k].length));
          }
        });
        return {
          ...prev,
          failure_samples: fs,
          failure_buckets: fb,
          failing_gate: Math.max(0, (prev.failing_gate || 1) - 1),
        };
      });
    } catch (err) {
      toast.error(`Failed to reclassify ${t}`, {
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        duration: 5000,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Round P112a (Jul 2026) — Bulk reclassify → Pending Non-Div queue.
  // Iterates over checked tickers in alphabetical order, calls the
  // same single-ticker mark-non-dividend endpoint serially. On failure
  // we toast and continue (matches AdminPendingDividendReview's bulk
  // pattern). Selected tickers are removed from the local report as
  // they succeed so the checkbox count / master state stays honest.
  const bulkReclassify = async () => {
    const tickers = [...selected].sort();
    if (tickers.length === 0) {
      toast.error('No tickers selected');
      return;
    }
    setBulkReclassifying(true);
    const tally = { ok: 0, fail: 0 };
    for (const tk of tickers) {
      setBusyTicker(tk);
      try {
        await axios.post(
          `${API}/admin/mark-non-dividend/${encodeURIComponent(tk)}`,
          { reason: 'audit_bulk_reclassify_non_div' },
          { withCredentials: true },
        );
        tally.ok += 1;
        toast.success(`${tk} → Pending Non-Div Review`, { duration: 2500 });
        setReport((prev) => {
          if (!prev) return prev;
          const fs = { ...(prev.failure_samples || {}) };
          const fb = { ...(prev.failure_buckets || {}) };
          Object.keys(fs).forEach((k) => {
            const before = fs[k].length;
            fs[k] = fs[k].filter((r) => r.ticker !== tk);
            if (fb[k] != null && fs[k].length !== before) {
              fb[k] = Math.max(0, fb[k] - (before - fs[k].length));
            }
          });
          return {
            ...prev,
            failure_samples: fs,
            failure_buckets: fb,
            failing_gate: Math.max(0, (prev.failing_gate || 1) - 1),
          };
        });
        setSelected((prev) => {
          const n = new Set(prev);
          n.delete(tk);
          return n;
        });
      } catch (err) {
        tally.fail += 1;
        toast.error(`Skipped ${tk}`, {
          description: err?.response?.data?.detail || err?.message || 'Reclassify failed',
          duration: 4000,
        });
      }
    }
    setBusyTicker(null);
    setBulkReclassifying(false);
    toast.success(
      `Bulk reclassify complete — ${tally.ok} queued, ${tally.fail} skipped`,
      { duration: 5000 },
    );
  };


  // Round 96c (Feb 2026) — Promote a flagged ticker INTO the locked
  // dividend DB. Hits the new
  // `/api/admin/dividend-gate/{t}/promote-to-dividend` endpoint which
  // re-fetches Massive + Yahoo, re-runs the 5-field gate, and locks
  // the row on pass. On failure (gate still failing after refresh),
  // the toast reports the missing fields so the admin knows whether
  // to Mark non-div or Trash instead. Optimistically strips the row
  // from the modal on success.
  const promoteToDividend = async (row) => {
    const t = row.ticker;
    setBusyTicker(t);
    try {
      const { data } = await axios.post(
        `${API}/admin/dividend-gate/${encodeURIComponent(t)}/promote-to-dividend`,
        {},
        { withCredentials: true },
      );
      // Round 98r (Jul 2026) — 200+{ok:false} handler for
      // Cloudflare-stripped 5xx (missing key / Massive fetch failed).
      if (data && data.ok === false) {
        toast.error(`Failed to promote ${t}`, {
          description: data.detail || 'Upstream failure — see server logs',
          duration: 6000,
        });
        return;
      }
      toast.success(`Promoted ${t}`, {
        description: `Locked ${data.locked_fields?.length || 0} fields. Source: Massive.`,
        duration: 4000,
      });
      setReport((prev) => {
        if (!prev) return prev;
        const fs = { ...(prev.failure_samples || {}) };
        const fb = { ...(prev.failure_buckets || {}) };
        Object.keys(fs).forEach((k) => {
          const before = fs[k].length;
          fs[k] = fs[k].filter((r) => r.ticker !== t);
          if (fb[k] != null && fs[k].length !== before) {
            fb[k] = Math.max(0, fb[k] - (before - fs[k].length));
          }
        });
        return {
          ...prev,
          failure_samples: fs,
          failure_buckets: fb,
          failing_gate: Math.max(0, (prev.failing_gate || 1) - 1),
          passing_gate: (prev.passing_gate || 0) + 1,
        };
      });
    } catch (err) {
      toast.error(`Failed to promote ${t}`, {
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        duration: 6000,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Round 96c (Feb 2026) — Ticker click opens the Stock Modal via the
  // Dashboard's shared handler. We close the audit modal first so the
  // Stock Modal's backdrop overlay reads as the topmost layer; the
  // audit list isn't lost — the Dashboard preserves it on its own
  // state, and the admin can re-open the audit afterwards. Mirrors
  // the AdminPendingDividendReviewButton flow exactly.
  // Round P111z (Feb 28, 2026) — Keep the audit modal open when the
  // admin drills into a ticker via `onStockClick`. Previously we
  // called `setOpen(false)` here, which was fine for the pending-review
  // flow but confusing for this audit list — the admin often works
  // through 20-40 rows sequentially and wants the list to stay put
  // so they can resume where they left off after closing the
  // StockModal. StockModal renders in a portal above ours, so leaving
  // this modal mounted is safe.
  const handleTickerClick = (ticker) => {
    if (!ticker) return;
    onStockClick?.(ticker);
  };

  return (
    <>
      <button
        type="button"
        onClick={runAudit}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: audit stock_cache against the 5-field dividend gate. Read-only — surfaces tickers whose data has drifted."
        data-testid="admin-dividend-audit-fire"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldAlert className="w-3 h-3" />}
        Audit dividend gate
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="admin-dividend-audit-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
              <div className="flex-1">
                <div className="text-sm font-bold">Dividend-gate audit</div>
                <div className="text-[10px] text-muted-foreground">
                  Read-only · runs the 5-field check over every ticker in stock_cache.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                data-testid="admin-dividend-audit-close"
                aria-label="Close audit modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Auditing stock_cache…
                </div>
              )}

              {report && (
                <>
                  {/* Summary stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="border border-border/60 rounded-md px-3 py-2">
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Audited</div>
                      <div className="text-sm font-bold font-mono" data-testid="audit-total">
                        {report.total_in_cache?.toLocaleString?.() ?? report.total_in_cache}
                      </div>
                    </div>
                    <div className="border border-emerald-300/60 dark:border-emerald-700/60 rounded-md px-3 py-2 bg-emerald-50 dark:bg-emerald-950/40">
                      <div className="text-[9px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Passing</div>
                      <div className="text-sm font-bold font-mono text-emerald-700 dark:text-emerald-300" data-testid="audit-passing">
                        {report.passing_gate?.toLocaleString?.() ?? report.passing_gate}
                      </div>
                    </div>
                    <div className="border border-rose-300/60 dark:border-rose-700/60 rounded-md px-3 py-2 bg-rose-50 dark:bg-rose-950/40">
                      <div className="text-[9px] uppercase tracking-wide text-rose-700 dark:text-rose-300">Failing</div>
                      <div className="text-sm font-bold font-mono text-rose-700 dark:text-rose-300" data-testid="audit-failing">
                        {report.failing_gate?.toLocaleString?.() ?? report.failing_gate}
                      </div>
                    </div>
                    <div className="border border-border/60 rounded-md px-3 py-2">
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Multi-field</div>
                      <div className="text-sm font-bold font-mono" data-testid="audit-multi">
                        {report.multi_field_failures?.toLocaleString?.() ?? report.multi_field_failures}
                      </div>
                    </div>
                  </div>

                  {/* Per-bucket counts */}
                  {report.failure_buckets && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(report.failure_buckets).map(([k, v]) => (
                        <span
                          key={k}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                            v > 0
                              ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {BUCKET_LABELS[k] || k}: <span className="font-mono font-bold">{v}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Round 93 — Massive-authority coverage signal */}
                  {report.massive_authority && (
                    <div
                      className="border border-violet-300/60 dark:border-violet-700/60 rounded-md px-3 py-2 bg-violet-50 dark:bg-violet-950/30"
                      data-testid="audit-massive-authority"
                    >
                      <div className="text-[9px] uppercase tracking-wide text-violet-700 dark:text-violet-300 mb-1">
                        Massive authority coverage
                      </div>
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                        <span className="text-foreground">
                          <span className="font-mono font-bold">
                            {report.massive_authority.dividend_authoritative_source_massive?.toLocaleString?.() ?? '—'}
                          </span>{' '}
                          / {report.total_in_cache?.toLocaleString?.() ?? '—'}{' '}
                          <span className="text-muted-foreground">tagged authoritative=massive</span>
                          {' '}
                          <span className="font-mono text-violet-700 dark:text-violet-300">
                            ({report.massive_authority.coverage_pct}%)
                          </span>
                        </span>
                        {typeof report.massive_authority.dividend_yield_locked === 'number' && (
                          <span className="text-muted-foreground">
                            yield-locked:{' '}
                            <span className="font-mono font-bold text-foreground">
                              {report.massive_authority.dividend_yield_locked.toLocaleString()}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* No-failures state */}
                  {report.failing_gate === 0 && (
                    <div className="flex items-center gap-2 px-3 py-4 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      All {report.total_in_cache?.toLocaleString?.()} tickers pass the dividend gate. 🎉
                    </div>
                  )}

                  {/* Round P112a — Bulk-reclassify toolbar. Master
                      checkbox toggles every visible failing row across
                      all buckets; the button iterates the checked set
                      alphabetically through mark-non-dividend. */}
                  {report.failure_samples && visibleTickers.length > 0 && (
                    <div className="flex items-center gap-3 px-3 py-2 border border-border/60 bg-muted/10 rounded-md">
                      <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          ref={(el) => {
                            if (el) {
                              const someChecked = visibleTickers.some((t) => selected.has(t));
                              el.indeterminate = !allVisibleSelected && someChecked;
                            }
                          }}
                          onChange={toggleAllVisible}
                          disabled={bulkReclassifying}
                          className="w-3.5 h-3.5 accent-amber-500 cursor-pointer"
                          data-testid="audit-master-checkbox"
                          title={allVisibleSelected ? 'Uncheck all visible' : 'Check all visible'}
                        />
                        <span>Select all visible ({visibleTickers.length})</span>
                      </label>
                      <span className="flex-1" />
                      <button
                        type="button"
                        onClick={bulkReclassify}
                        disabled={bulkReclassifying || selected.size === 0}
                        data-testid="audit-bulk-reclassify"
                        title="Send every checked ticker to Pending Non-Dividend Review. Failures are toasted and skipped."
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {bulkReclassifying
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <TrendingUp className="w-3 h-3" />}
                        Reclassify Checked ({selected.size})
                      </button>
                    </div>
                  )}

                  {/* Failure samples grouped by bucket */}
                  {report.failure_samples && (
                    <div className="space-y-2">
                      {Object.entries(report.failure_samples).map(([bucket, samples]) => (
                        <FailureBucket
                          key={bucket}
                          label={BUCKET_LABELS[bucket] || bucket}
                          samples={samples}
                          totalInBucket={report.failure_buckets?.[bucket] ?? samples.length}
                          onBlocklist={(row) => setConfirmBlocklist(row)}
                          onMarkNonDiv={markNonDiv}
                          onPromote={promoteToDividend}
                          onTickerClick={handleTickerClick}
                          busyTicker={busyTicker}
                          selected={selected}
                          onToggle={toggleOne}
                          disableChecks={bulkReclassifying}
                        />
                      ))}
                    </div>
                  )}

                  <div className="text-[10px] text-muted-foreground pt-2 border-t border-border/40">
                    Audited at {report.audited_at} · samples capped at 25 per bucket.
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-border/60 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={runAudit}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
                data-testid="admin-dividend-audit-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldAlert className="w-3 h-3" />}
                Re-run audit
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[11px] px-2.5 py-1 rounded-md border border-border hover:bg-muted"
                data-testid="admin-dividend-audit-dismiss"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Round 94f — Confirmation modal for permanent blocklist removal. */}
      {confirmBlocklist && (
        <div
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmBlocklist(null)}
          data-testid="audit-blocklist-confirm-modal"
        >
          <div
            className="bg-background border border-rose-500/60 rounded-lg shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border/60 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-rose-600" />
              <div className="text-sm font-bold">Permanently blocklist ticker?</div>
            </div>
            <div className="px-5 py-4 text-[12px] text-muted-foreground space-y-2">
              <div>
                <span className="font-mono font-bold text-foreground">{confirmBlocklist.ticker}</span>
                {' — '}
                <span>{confirmBlocklist.name}</span>
              </div>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Deletes the row from <span className="font-mono">stock_cache</span> and all caches.</li>
                <li>Removes any user portfolio / watchlist entries pointing at it.</li>
                <li>Adds it to the <span className="font-mono">dynamic_blocklist</span> — survives redeploys.</li>
                <li>Future refreshes (Yahoo, Massive, scheduler) cannot re-insert it.</li>
              </ul>
              <p className="pt-2 text-rose-700 dark:text-rose-400 font-semibold">
                This is permanent. Use only for tickers that clearly don&apos;t belong.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border/60 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmBlocklist(null)}
                className="px-3 py-1.5 rounded-md text-[12px] font-bold border border-border hover:bg-muted"
                data-testid="audit-blocklist-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const row = confirmBlocklist;
                  setConfirmBlocklist(null);
                  blocklistTicker(row);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="audit-blocklist-confirm"
              >
                <Trash2 className="w-3 h-3" />
                Remove and blocklist
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
